import React, { ReactNode, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { TooltipProps } from './types';
import { Container } from './components/styled';
import { TooltipPopover } from './components/TooltipPopover';
import { Portal } from '../Portal';

const WithOptionalPortal: React.FC<{ usePortal: boolean; children: ReactNode }> = ({
  usePortal = false,
  children,
  // eslint-disable-next-line react/jsx-no-useless-fragment
}) => (usePortal ? <Portal>{children}</Portal> : <>{children}</>);

export const Tooltip = ({
  children,
  text,
  content,
  config,
  reversed = false,
  className,
  withTriangle,
  initialShouldShow = false,
  useReactPortal = true,
}: TooltipProps): JSX.Element | null => {
  const [shouldShow, setShouldShow] = useState(initialShouldShow);
  const [hoverableElement, setHoverableElement] = useState<HTMLDivElement | null>(null);

  const classNames = {
    container: className && `${className} ${className}-container`,
    tip: className && `${className}-tip`,
    wrap: className && `${className}-wrap`,
    triangle: className && `${className}-triangle`,
    hoverable: className && `${className}-hoverable`,
  };

  const hasText = text ? text.trim().length > 0 : false;
  const hasContent = !!(content || hasText);

  if (!hasContent) return children;

  const show = () => setShouldShow(true);
  const hide = () => setShouldShow(false);

  return (
    <Container className={classNames.container}>
      <AnimatePresence>
        {shouldShow && (
          <WithOptionalPortal usePortal={useReactPortal}>
            <TooltipPopover
              key="tooltip-popover"
              classNames={classNames}
              hoverableElement={hoverableElement}
              config={config}
              reversed={reversed}
              withTriangle={withTriangle}
              text={text}
              content={content}
            />
          </WithOptionalPortal>
        )}
      </AnimatePresence>

      <Container
        ref={setHoverableElement}
        className={classNames.hoverable}
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        {children}
      </Container>
    </Container>
  );
};
